import React, { useCallback, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { navigate } from "@reach/router";
import styled from "styled-components";
import { useAuth } from "react-use-auth";
import DOMPurify from "dompurify";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { Reference, references } from "../../utils/reference";
import { LayoutDefault } from "../Layouts/LayoutDefault";
// import { FileUploader } from "../common/FileUploader/FileUploader";
import { ContentSection } from "../common/ContentSection/ContentSection";
import { Button } from "../common/Button/Button";
import { Dropdown } from "../common/Dropdown/Dropdown";
import { FileDropZone } from "../common/FileUploader/FileDropZone";
import { ConfirmationModal } from "../common/Modal/ConfirmationModal";
import {
  parseSinglePdfFromDataUrl,
  parseZipPdfsFromFile,
  ParsedPdf,
  OcrProgressEvent,
} from "../../utils/parseZipPdfs";
import { validateLiteratureReferences } from "../../utils/literatureReferencesValidation";
import type {
  LiteratureValidationResult,
  OverviewValidation,
} from "../../utils/literatureReferencesValidation";
import { runPromptWithProvider } from "../../utils/llmClient";
import { runPromptsForFiles } from "../../utils/runPromptsForFiles";
import { readScannedPageMode } from "../../utils/aiCommon";
import { styledTheme } from "../../theme/theme";
import { ROUTES } from "../../constants/routeConstants";

type UploadedFile = {
  fileName: string;
  fileSize: number;
};

type PageStatsSummary = {
  totalPages: number;
  textPages: number;
  scannedPages: number;
  fileCount: number;
};

type PromptResult = {
  reference: Reference;
  text: string;
  fileName?: string;
  filename?: string;
  model?: string;
  inputTruncated?: boolean;
};

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"] as const;
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const value = bytes / 1024 ** i;
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

function summarizePageStats(parsed: ParsedPdf[]): PageStatsSummary {
  return parsed.reduce(
    (acc, p) => {
      const stats = p.pageStats;
      if (!stats) return acc;
      return {
        totalPages: acc.totalPages + stats.totalPages,
        textPages: acc.textPages + stats.textPages,
        scannedPages: acc.scannedPages + stats.scannedPages,
        fileCount: acc.fileCount + 1,
      };
    },
    { totalPages: 0, textPages: 0, scannedPages: 0, fileCount: 0 }
  );
}

function OverviewValidationBlock({
  label,
  overview,
  validModuleLabel,
}: {
  label: string;
  overview: OverviewValidation;
  validModuleLabel: string;
}): JSX.Element {
  const { fileName, sectionFound, citedCitations, validItems, missingItems } =
    overview;
  let summary: string;
  if (!sectionFound) {
    summary = "Literature References section not found.";
  } else if (missingItems.length === 0) {
    summary = "All references valid.";
  } else {
    summary = `${missingItems.length} reference(s) missing from ${validModuleLabel}.`;
  }
  return (
    <div style={{ fontSize: 14 }}>
      <h2>{label}</h2>
      <div style={{ color: "#555", marginBottom: 6 }}>File: {fileName}</div>
      <div style={{ marginBottom: 6 }}>{summary}</div>
      {missingItems.length > 0 && (
        <div style={{ color: "#c00" }}>
          <strong>
            Missing ({missingItems.length}) — not matched to a file in{" "}
            {validModuleLabel}:
          </strong>
          <ul style={{ margin: "4px 0 0 16px", paddingLeft: 8 }}>
            {missingItems.map((item) => (
              <li key={`missing-${item.refName}`} style={{ marginBottom: 4 }}>
                <strong>{item.refName}</strong>
                {/* {item.citation &&
                      ` — ${item.citation.length > 150 ? `${item.citation.slice(0, 150)}…` : item.citation}`} */}
              </li>
            ))}
          </ul>
        </div>
      )}
      {sectionFound && citedCitations.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {validItems.length > 0 && (
            <div>
              <strong>
                Matched references ({validItems.length}) — present in{" "}
                {validModuleLabel}:
              </strong>
              <ul style={{ margin: "4px 0 0 16px", paddingLeft: 8 }}>
                {validItems.map((item) => (
                  <li key={item.refName} style={{ marginBottom: 4 }}>
                    <strong>{item.refName}</strong>
                    {item.citation &&
                      ` — ${
                        item.citation.length > 150
                          ? `${item.citation.slice(0, 150)}…`
                          : item.citation
                      }`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const Regulatory = (): JSX.Element => {
  const { authResult } = useAuth();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [promptResults, setPromptResults] = useState<PromptResult[]>([]);
  const [promptsInFlight, setPromptsInFlight] = useState<number>(0);
  const [progressTotal, setProgressTotal] = useState<number>(0);
  const [progressCompleted, setProgressCompleted] = useState<number>(0);
  const [progressLastFile, setProgressLastFile] = useState<string>("");
  const [pageStatsSummary, setPageStatsSummary] =
    useState<PageStatsSummary | null>(null);
  const [ocrProgress, setOcrProgress] = useState<OcrProgressEvent | null>(null);

  const [literatureValidation, setLiteratureValidation] =
    useState<LiteratureValidationResult | null>(null);
  const [singlePdfFile, setSinglePdfFile] = useState<{
    dataUrl: string;
    fileName: string;
  } | null>(null);
  const [selectedReferenceIndex, setSelectedReferenceIndex] = useState<
    number | null
  >(null);
  const [singlePdfAnalyzing, setSinglePdfAnalyzing] = useState<boolean>(false);
  const [isClearResultsModalOpen, setIsClearResultsModalOpen] =
    useState<boolean>(false);

  const referenceDropdownOptions = useMemo(
    () =>
      references.map((ref, i) => ({
        value: String(i),
        label: `${ref.ectdTitle} (${ref.ectdKey})`,
      })),
    []
  );

  const latestFile = files.length ? files[files.length - 1] : undefined;

  const sortedResults = useMemo(
    () =>
      [...promptResults].sort((a, b) =>
        (a.fileName || a.filename || "").localeCompare(
          b.fileName || b.filename || ""
        )
      ),
    [promptResults]
  );

  const hasReportResults = !!(latestFile || promptResults.length > 0);

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  const processZip = useCallback(
    async (file: File): Promise<void> => {
      setFiles((prev) => [
        ...prev,
        { fileName: file.name, fileSize: file.size },
      ]);
      setPromptsInFlight((c) => c + 1);
      setOcrProgress(null);
      try {
        // Read the ZIP as an ArrayBuffer — no base64 data URL / JSON
        // round-trip, which overflows the browser string limit on large archives.
        const parsed = await parseZipPdfsFromFile(file, {
          authToken: authResult?.accessToken as string | undefined,
          scannedPageMode: readScannedPageMode(),
          onOcrProgress: (event) => {
            setOcrProgress(event);
          },
        });
        setPageStatsSummary(summarizePageStats(parsed));
        setLiteratureValidation(validateLiteratureReferences(parsed));
        setProgressTotal(0);
        setProgressCompleted(0);
        setProgressLastFile("");
        setOcrProgress(null);
        const results = await runPromptsForFiles(
          parsed,
          (completed, total, fileName) => {
            setProgressTotal(total);
            setProgressCompleted(completed);
            if (fileName) setProgressLastFile(fileName);
          },
          { authToken: authResult?.accessToken as string | undefined }
        );
        if (Array.isArray(results)) {
          setPromptResults((prev) => prev.concat(results as PromptResult[]));
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        toast.error(`eCTD package upload failed: ${message}`);
      } finally {
        setPromptsInFlight((c) => Math.max(0, c - 1));
        setOcrProgress(null);
      }
    },
    [authResult?.accessToken]
  );

  const zipUploadDisabled = hasReportResults || singlePdfAnalyzing;

  const handleZipDrop = useCallback(
    (acceptedFiles: File[]): void => {
      const file = acceptedFiles[0];
      if (!file || zipUploadDisabled) return;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      processZip(file);
    },
    [processZip, zipUploadDisabled]
  );

  const {
    getRootProps: getZipRootProps,
    getInputProps: getZipInputProps,
    isDragAccept: isZipDragAccept,
    open: openZip,
  } = useDropzone({
    accept: "application/zip,.zip,application/x-zip-compressed",
    maxFiles: 1,
    disabled: zipUploadDisabled,
    onDrop: handleZipDrop,
    onDropRejected: (rejected): void => {
      if (rejected.length > 0) {
        toast.error("Please upload a ZIP file only.");
      }
    },
  });

  const { getRootProps, getInputProps, isDragAccept, open } = useDropzone({
    accept: "application/pdf,.pdf",
    maxFiles: 1,
    onDrop: useCallback((acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = typeof reader.result === "string" ? reader.result : "";
        if (dataUrl) setSinglePdfFile({ dataUrl, fileName: file.name });
      };
      reader.readAsDataURL(file);
    }, []),
  });

  const handleAnalyzeSinglePdf = useCallback(async () => {
    if (
      !singlePdfFile ||
      selectedReferenceIndex === null ||
      selectedReferenceIndex < 0 ||
      selectedReferenceIndex >= references.length
    )
      return;
    const reference = references[selectedReferenceIndex];
    setSinglePdfAnalyzing(true);
    try {
      const parsed = await parseSinglePdfFromDataUrl(
        singlePdfFile.dataUrl,
        singlePdfFile.fileName,
        {
          authToken: authResult?.accessToken as string | undefined,
          scannedPageMode: readScannedPageMode(),
        }
      );
      const parsedWithRef = {
        ...parsed,
        ectdKey: reference.ectdKey,
        ectdTitle: reference.ectdTitle,
      };
      const result = await runPromptWithProvider(parsedWithRef, reference, {
        provider: "openrouter",
        authToken: authResult?.accessToken as string | undefined,
      });
      setPromptResults((prev) =>
        prev.concat({
          reference,
          text: result.text,
          fileName: result.fileName,
          model: result.model,
          ...(result.inputTruncated ? { inputTruncated: true } : {}),
        })
      );
    } catch (e) {
      console.warn(e);
    } finally {
      setSinglePdfAnalyzing(false);
    }
  }, [singlePdfFile, selectedReferenceIndex, authResult?.accessToken]);

  const handleClearResults = useCallback((): void => {
    setPromptResults([]);
    setLiteratureValidation(null);
    setFiles([]);
    setPageStatsSummary(null);
    setOcrProgress(null);
    setSinglePdfFile(null);
    setSelectedReferenceIndex(null);
    setProgressTotal(0);
    setProgressCompleted(0);
    setProgressLastFile("");
  }, []);

  return (
    <LayoutDefault pageTitle="Regulatory">
      <Container>
        <ContentSection
          title="Regulatory"
          stepNumber={1}
          wrapCardsCallback={(): void => {}}
          buttons={[]}
          fullWidth
        >
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 16,
            }}
          >
            <Button
              label="Loose PDF set → eCTD mapping"
              onClick={(): void => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                navigate(ROUTES.REGULATORY_ECTD_MAPPING);
              }}
            />
          </div>
          <div
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              marginTop: 24,
              padding: 16,
              background: "#fafafa",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>
              Upload the full eCTD package in a ZIP file
            </h3>
            <FileDropZone
              rootProps={getZipRootProps({ isDragAccept: isZipDragAccept })}
              inputProps={getZipInputProps()}
              open={openZip}
              disabled={zipUploadDisabled}
            />

            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                marginTop: 24,
                padding: 16,
                background: "#fafafa",
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: 8 }}>Upload summary</h3>
              {latestFile ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    rowGap: 6,
                  }}
                >
                  <div>
                    <strong>File name</strong>
                  </div>
                  <div>{latestFile.fileName}</div>
                  <div>
                    <strong>Size</strong>
                  </div>
                  <div>{formatBytes(latestFile.fileSize)}</div>
                  <div>
                    <strong>Total files</strong>
                  </div>
                  <div>{files.length}</div>
                  {pageStatsSummary && pageStatsSummary.totalPages > 0 && (
                    <>
                      <div>
                        <strong>PDF pages</strong>
                      </div>
                      <div>
                        {pageStatsSummary.totalPages} total ·{" "}
                        {pageStatsSummary.textPages} text ·{" "}
                        {pageStatsSummary.scannedPages} scanned
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ color: "#666" }}>No files uploaded yet.</div>
              )}
              {ocrProgress && ocrProgress.phase === "classify" && (
                <div style={{ marginTop: 12, padding: 12 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>Classifying PDF files</div>
                    <div style={{ color: "#333" }}>
                      {ocrProgress.fileCount > 0
                        ? `${Math.floor(
                            (ocrProgress.filesCompleted /
                              ocrProgress.fileCount) *
                              100
                          )}%`
                        : "0%"}
                    </div>
                  </div>
                  <div
                    style={{
                      height: 10,
                      background: "#eee",
                      borderRadius: 6,
                      overflow: "hidden",
                    }}
                    aria-label="PDF classification progress"
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${
                          ocrProgress.fileCount > 0
                            ? (ocrProgress.filesCompleted /
                                ocrProgress.fileCount) *
                              100
                            : 0
                        }%`,
                        background: styledTheme.newColors.primary.basic,
                        transition: "width 300ms ease",
                      }}
                    />
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: "#555" }}>
                    {`${ocrProgress.filesCompleted} of ${ocrProgress.fileCount} files classified`}
                    {ocrProgress.label ? ` · ${ocrProgress.label}` : ""}
                  </div>
                </div>
              )}
              {ocrProgress && ocrProgress.phase === "extract" && (
                <div style={{ marginTop: 12, padding: 12 }}>
                  <div style={{ marginBottom: 16 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 6,
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>Pages</div>
                      <div style={{ color: "#333" }}>
                        {ocrProgress.overallTotal > 0
                          ? `${Math.floor(
                              (ocrProgress.overallCompleted /
                                ocrProgress.overallTotal) *
                                100
                            )}%`
                          : "Counting pages…"}
                      </div>
                    </div>
                    <div
                      style={{
                        height: 10,
                        background: "#eee",
                        borderRadius: 6,
                        overflow: "hidden",
                      }}
                      aria-label="OCR pages progress"
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${
                            ocrProgress.overallTotal > 0
                              ? (ocrProgress.overallCompleted /
                                  ocrProgress.overallTotal) *
                                100
                              : 0
                          }%`,
                          background: styledTheme.newColors.primary.basic,
                          transition: "width 300ms ease",
                        }}
                      />
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: "#555" }}>
                      {ocrProgress.overallTotal > 0
                        ? `${ocrProgress.overallCompleted} of ${ocrProgress.overallTotal} pages processed`
                        : "Counting pages…"}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 6,
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>Files</div>
                      <div style={{ color: "#333" }}>
                        {ocrProgress.fileCount > 0
                          ? `${Math.floor(
                              (ocrProgress.filesCompleted /
                                ocrProgress.fileCount) *
                                100
                            )}%`
                          : "0%"}
                      </div>
                    </div>
                    <div
                      style={{
                        height: 10,
                        background: "#eee",
                        borderRadius: 6,
                        overflow: "hidden",
                      }}
                      aria-label="OCR files progress"
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${
                            ocrProgress.fileCount > 0
                              ? (ocrProgress.filesCompleted /
                                  ocrProgress.fileCount) *
                                100
                              : 0
                          }%`,
                          background: styledTheme.newColors.primary.basic,
                          transition: "width 300ms ease",
                        }}
                      />
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: "#555" }}>
                      {`${ocrProgress.filesCompleted} of ${ocrProgress.fileCount} files processed`}
                      {ocrProgress.label ? ` · ${ocrProgress.label}` : ""}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              marginTop: 24,
              padding: 16,
              background: "#fafafa",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>
              Or analyze a single PDF
            </h3>
            <p style={{ margin: "0 0 12px", color: "#555", fontSize: 14 }}>
              Upload one PDF file and select which reference document it should
              be compared against.
            </p>
            <SinglePdfRow>
              <FileDropZone
                rootProps={getRootProps({ isDragAccept })}
                inputProps={getInputProps()}
                open={open}
                disabled={singlePdfAnalyzing || hasReportResults}
              />
              <SinglePdfControls>
                <Dropdown
                  options={referenceDropdownOptions}
                  value={
                    selectedReferenceIndex !== null
                      ? referenceDropdownOptions[selectedReferenceIndex]
                      : null
                  }
                  onChange={(option): void => {
                    const idx =
                      option && typeof option.value === "string"
                        ? parseInt(option.value, 10)
                        : NaN;
                    setSelectedReferenceIndex(
                      Number.isFinite(idx) &&
                        idx >= 0 &&
                        idx < references.length
                        ? idx
                        : null
                    );
                  }}
                  placeholder="Select reference document"
                  isClearable
                />
                <Button
                  label={
                    singlePdfAnalyzing
                      ? "Analyzing… PLEASE WAIT"
                      : "Analyze single PDF"
                  }
                  onClick={handleAnalyzeSinglePdf}
                  disabled={
                    !singlePdfFile ||
                    selectedReferenceIndex === null ||
                    singlePdfAnalyzing ||
                    hasReportResults
                  }
                />
              </SinglePdfControls>
            </SinglePdfRow>
            {singlePdfFile && (
              <div style={{ marginTop: 8, fontSize: 13, color: "#333" }}>
                Selected file: <strong>{singlePdfFile.fileName}</strong>
              </div>
            )}
          </div>

          {(latestFile || promptResults.length > 0) && (
            <PrintContainer
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                marginTop: 24,
                padding: 16,
                background: "#fafafa",
                position: "relative",
                minHeight: 120,
              }}
              ref={printRef}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: 8 }}>
                  Document analysis results
                </h3>
                <NoPrint
                  style={{ display: "flex", gap: 8, alignItems: "center" }}
                >
                  <Button
                    label="Clear results"
                    onClick={(): void => setIsClearResultsModalOpen(true)}
                  />
                  <Button
                    label="Prepare printout"
                    onClick={handlePrint}
                    disabled={!sortedResults.length}
                  />
                </NoPrint>
              </div>
              {promptsInFlight > 0 && (
                <>
                  {progressTotal > 0 && (
                    <div
                      style={{
                        padding: 12,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 6,
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>Running analysis</div>
                        <div style={{ color: "#333" }}>
                          {Math.floor(
                            (progressCompleted / progressTotal) * 100
                          )}
                          %
                        </div>
                      </div>
                      <div
                        style={{
                          height: 10,
                          background: "#eee",
                          borderRadius: 6,
                          overflow: "hidden",
                        }}
                        aria-label="Analysis progress"
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${
                              (progressCompleted / progressTotal) * 100
                            }%`,
                            background: styledTheme.newColors.primary.basic,
                            transition: "width 300ms ease",
                          }}
                        />
                      </div>
                      <div
                        style={{ marginTop: 6, fontSize: 12, color: "#555" }}
                      >
                        {progressCompleted} of {progressTotal} files processed
                        {progressLastFile ? ` • Last: ${progressLastFile}` : ""}
                      </div>
                    </div>
                  )}
                </>
              )}
              {sortedResults.length ? (
                <div>
                  {sortedResults.map((r) => {
                    const name = r.fileName || "Unknown file";
                    const referenceUrl = r.reference.sourceUrl;
                    const referenceName = r.reference.sourceName;
                    const safeHtml = DOMPurify.sanitize(r.text || "", {
                      USE_PROFILES: { html: true },
                    });
                    return (
                      <div
                        key={`${name}-${r.reference.ectdKey}-${r.reference.sourceName}`}
                        style={{ marginBottom: 12 }}
                      >
                        <h1>File: {name}</h1>
                        {r.inputTruncated && (
                          <div
                            style={{
                              background: "#fff8e1",
                              border: "1px solid #ffe082",
                              borderRadius: 6,
                              padding: "8px 12px",
                              marginBottom: 8,
                              color: "#7a5c00",
                              fontSize: 14,
                            }}
                          >
                            The prompt was shortened because it was too long, so
                            this result may not cover the full document.
                          </div>
                        )}
                        <div>
                          This document was compared to the following reference:{" "}
                          <a
                            href={referenceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {referenceName}
                          </a>
                        </div>
                        <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: "#666" }}>
                  Waiting for document analysis...
                  <br />
                  It usually takes about 2-3 minutes.
                </div>
              )}

              {literatureValidation && (
                <div>
                  <h1>Literature references check</h1>
                  {literatureValidation.nonclinical ||
                  literatureValidation.clinical ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                      }}
                    >
                      {literatureValidation.nonclinical && (
                        <OverviewValidationBlock
                          label="Nonclinical overview (M2.4)"
                          overview={literatureValidation.nonclinical}
                          validModuleLabel="M4.3 (literature references)"
                        />
                      )}
                      {!literatureValidation.nonclinical && (
                        <div style={{ color: "#666", fontSize: 14 }}>
                          Nonclinical overview (M2.4): overview not found in
                          package.
                        </div>
                      )}
                      {literatureValidation.clinical && (
                        <OverviewValidationBlock
                          label="Clinical overview (M2.5)"
                          overview={literatureValidation.clinical}
                          validModuleLabel="M5.4 (literature references)"
                        />
                      )}
                      {!literatureValidation.clinical && (
                        <div style={{ color: "#666", fontSize: 14 }}>
                          Clinical overview (M2.5): overview not found in
                          package.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ color: "#666", fontSize: 14 }}>
                      No overview documents (M2.4 / M2.5) found in package.
                    </div>
                  )}
                </div>
              )}
            </PrintContainer>
          )}

          <ConfirmationModal
            isOpen={isClearResultsModalOpen}
            title="Clear results?"
            message="This will clear all report results and re-enable file uploads. Do you want to continue?"
            closeModal={(): void => setIsClearResultsModalOpen(false)}
            acceptModal={(): void => {
              handleClearResults();
              setIsClearResultsModalOpen(false);
            }}
            yesButtonText="Clear"
          />
        </ContentSection>
      </Container>
    </LayoutDefault>
  );
};

export default Regulatory;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;

  > div {
    margin-right: 24px;
    margin-bottom: 24px;
  }
`;

const NoPrint = styled.div`
  @media print {
    display: none;
  }
`;

const PrintContainer = styled.div`
  @media print {
    display: block;

    @page {
      margin: 1cm;
    }
  }
`;

const SinglePdfRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  align-items: flex-start;
  gap: 16px;
`;

const SinglePdfControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;
